// ─── Tab · Planes iniciales requeridos por BREEAM ────────────────────────
//
// Editor estructurado de los planes que el contratista debe entregar
// al arranque de la obra según BREEAM ES NC 2015. Fase 3 del Cockpit
// del Contratista. Un plan por tipo por expediente.
//
// FASE 3 · Sesión de hoy · PPCAI (Plan de Prevención y Control de la
// Calidad del Aire Interior · criterio SYB 02). Los otros planes (PGR,
// GST03, Puesta en Servicio, GUE, cartas) vendrán en próximas sesiones
// pero la tabla y el componente ya están preparados para todos.

import { useState, useEffect, useCallback } from 'react'
import {
  Wind, ClipboardText, TrashSimple, Sparkle,
  FloppyDisk, ArrowClockwise, DownloadSimple, FilePdf, Plus,
  CheckCircle,
} from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import { callBreeamAnalyzer, parseAiJson } from '../../lib/breeamAnalyzer'
import { exportPpcaiToWord } from '../../lib/exportPpcaiToWord'
import { exportPpcaiToPdf } from '../../lib/exportPpcaiToPdf'

// ── Paleta e estilos compartidos (mismos que ProjectDetails / Cockpit) ────
const C = {
  bg: '#FAF7F3', card: 'white', border: '#DDD5C8', text: '#3B2F2F',
  muted: '#7A6A5A', accent: '#1A3A4E', warn: '#D4614A', olive: '#8D9A2E',
  blueLight: '#EBF3F8', amber: '#C9A961',
}
const FF = "'DM Sans', sans-serif"
const LABEL_STYLE = { fontSize: '0.78rem', color: C.muted, fontWeight: '500', marginBottom: '0.25rem', display: 'block', fontFamily: FF }
const INPUT_STYLE = {
  width: '100%', padding: '0.45rem 0.6rem', fontSize: '0.82rem',
  border: `1px solid ${C.border}`, borderRadius: '4px', background: 'white',
  color: C.text, fontFamily: FF, outline: 'none',
}

// ── Catálogo de tipos de plan (Fase 3 al completo) ───────────────────────
const TIPOS_PLAN = [
  {
    code: 'ppcai',
    label: 'Plan de Prevención y Control de la Calidad del Aire Interior',
    corto: 'PPCAI',
    criterios: ['SyB 02'],
    icon: Wind,
    disponible: true,   // Fase 3 sesión 1
    descripcion: 'Plan que el contratista implementa durante la construcción para minimizar la contaminación del aire interior · protección HVAC, control de fuentes emisoras, rutas de contaminantes, limpieza, materiales absorbentes, cronograma.',
  },
  {
    code: 'pgr',
    label: 'Plan de Gestión de Residuos + Auditoría pre-ejecución',
    corto: 'PGR',
    criterios: ['RSD 01'],
    icon: ClipboardText,
    disponible: false,
    descripcion: 'Próxima sesión · calcula residuos estimados por partida a partir del presupuesto y planifica su gestión.',
  },
  {
    code: 'gst03',
    label: 'Plan de Impacto en la Zona de Obras',
    corto: 'GST 03',
    criterios: ['GST 03'],
    icon: ClipboardText,
    disponible: false,
    descripcion: 'Próxima sesión.',
  },
  {
    code: 'puesta_servicio',
    label: 'Plan de Puesta en Servicio',
    corto: 'Puesta en servicio',
    criterios: ['GST 01'],
    icon: ClipboardText,
    disponible: false,
    descripcion: 'Próxima sesión.',
  },
  {
    code: 'gue',
    label: 'Guía de Información del Usuario del Edificio',
    corto: 'GUE',
    criterios: ['GST 04'],
    icon: ClipboardText,
    disponible: false,
    descripcion: 'Próxima sesión.',
  },
  {
    code: 'carta_compromiso',
    label: 'Cartas de compromiso y actas de nombramiento',
    corto: 'Compromisos',
    criterios: ['GST 03', 'RSD 01', 'SyB 02'],
    icon: ClipboardText,
    disponible: false,
    descripcion: 'Próxima sesión.',
  },
]

const TIPO_BY_CODE = Object.fromEntries(TIPOS_PLAN.map(t => [t.code, t]))

const ESTADO_LABELS = {
  borrador: { label: 'Borrador',   color: '#7A6A5A', bg: '#F5F0EA' },
  revision: { label: 'En revisión', color: '#C9A961', bg: '#F5E9D3' },
  final:    { label: 'Final',      color: '#8D9A2E', bg: '#E8F1D8' },
}

// ─── Esquema del PPCAI · Manual BREEAM ES NC 2015 ED.02 (SyB 2) ─────────
//
// FUENTE OFICIAL: Manual Técnico BREEAM ES Nueva Construcción 2015
// IPC-BREEAM-01-12 · ED.02 · 31/03/2017 · páginas 70-81
//
// El criterio SyB 2 se divide en 3 puntos + nivel ejemplar. El PPCAI
// es la piedra angular del punto 1 y consta de 5 ELEMENTOS OBLIGATORIOS
// (letras a-e del criterio 1 del manual). NO son los 6 apartados
// SMACNA del pliego LT02 de MAD2, que son una adaptación específica de
// PI Data Tech basada en LEED (los tratamos como sección opcional 6).

// 1) Los 5 elementos oficiales del PPCAI según el manual (criterio 1 a-e)
const PPCAI_ELEMENTOS_MANUAL = [
  {
    codigo: 'a',
    titulo: 'Eliminación de las fuentes de contaminación',
    referencia_manual: 'Manual BREEAM ES NC 2015 · SyB 2 criterio 1.a',
    medidas: [
      'Exigencia de fichas técnicas y certificados de emisión antes de la compra de cualquier producto de las 8 categorías A-H de la Tabla 13',
      'Prohibición contractual del uso de resinas de urea-formaldehído en el interior del edificio',
      'Selección de pinturas y barnices que cumplan la Directiva 2004/42/CE (fase acuosa preferentemente)',
      'Exclusión de productos con metales pesados regulados (antimonio, bario, cadmio, plomo, mercurio) y biocidas prohibidos (pentaclorofenol)',
      'Sustitución sistemática de acabados de alto contenido en disolventes por alternativas certificadas de bajo COV',
    ],
  },
  {
    codigo: 'b',
    titulo: 'Reducción y control de las fuentes de contaminación',
    referencia_manual: 'Manual BREEAM ES NC 2015 · SyB 2 criterio 1.b',
    medidas: [
      'Almacenamiento de productos emisivos en zonas ventiladas fuera del edificio',
      'Aplicación de productos con disolventes en horarios sin presencia de otros oficios',
      'Ventilación forzada localizada durante la aplicación de productos con disolventes',
      'Secado completo de productos emisivos antes de instalar acabados absorbentes sobre ellos',
      'Control diario del cumplimiento por parte del responsable BREEAM del contratista',
      'Registro fotográfico y actas de control semanal archivados en Procore o EDMS equivalente',
    ],
  },
  {
    codigo: 'c',
    titulo: 'Procedimientos de purga antes de la ocupación',
    referencia_manual: 'Manual BREEAM ES NC 2015 · SyB 2 criterio 1.c',
    medidas: [
      'Purga de ventilación mínima 72 horas tras la finalización de trabajos con disolventes en cada zona',
      'Renovación completa del aire del edificio antes de la puesta en servicio del HVAC definitivo',
      'Retirada de todos los residuos y polvo con aspirado HEPA antes del cierre de cada zona',
      'Inspección visual y protocolo de aceptación por zona antes de la ocupación',
      'Documentación fotográfica de las zonas purgadas y de las fichas de control archivada',
    ],
  },
  {
    codigo: 'd',
    titulo: 'Ensayos y análisis de terceros',
    referencia_manual: 'Manual BREEAM ES NC 2015 · SyB 2 criterio 1.d + criterios 8-11',
    medidas: [
      'Contratación de laboratorio acreditado ENAC para las mediciones post-construcción',
      'Medición de formaldehído por muestreo difusivo · UNE-ISO 16000-4:2006 · umbral OMS ≤ 100 μg/m³ durante 30 minutos',
      'Medición de COVT por muestreo difusivo · UNE-ISO 16000-6:2006 o UNE-EN ISO 16017-2:2004 · umbral BREEAM < 300 μg/m³ durante 8 horas',
      'Selección de estancias representativas (excluyendo aseos, escaleras, pasillos, salas de instalaciones)',
      'Sistemas de ventilación y calefacción en funcionamiento 12-24 h antes de la medición para alcanzar equilibrio',
      'Método de muestreo activo · UNE 77260-3:2004 como alternativa para formaldehído',
    ],
  },
  {
    codigo: 'e',
    titulo: 'Mantenimiento de la calidad del aire interior en uso',
    referencia_manual: 'Manual BREEAM ES NC 2015 · SyB 2 criterio 1.e',
    medidas: [
      'Plan de mantenimiento del sistema HVAC con periodicidad definida (filtros, conductos, difusores)',
      'Sustitución de filtros según ISO 16890 (categoría según calidad del aire exterior del emplazamiento)',
      'Entrega al propietario del protocolo de operación y mantenimiento de la ventilación',
      'Documento de recomendaciones al usuario sobre productos de limpieza, ambientadores y mobiliario de bajo COV',
      'Programa de inspecciones anuales de la calidad del aire interior tras la ocupación',
    ],
  },
]

// 2) Tabla 13 · 8 categorías de productos con requisitos COV/formaldehído
// Reproducida literal del manual BREEAM ES NC 2015 (páginas 77-79).
const PPCAI_CATEGORIAS_PRODUCTOS = [
  {
    codigo: 'A',
    label: 'Pinturas y barnices interiores',
    criterio_comportamiento: 'Límite de contenido de COV',
    normas_comportamiento: [
      'UNE-EN 13300:2002 · Pinturas y barnices · Materiales y sistemas de recubrimiento en fase acuosa para paredes y techos interiores · Clasificación',
      'Directiva UE 2004/42/CE de 21 de abril de 2004 · limitación de emisiones de COV en pinturas y barnices',
    ],
    normas_ensayo: [
      'UNE-EN ISO 11890-2:2013 · Método por cromatografía de gases',
    ],
    confirmacion_fabricante: 'La pintura debe ser resistente a hongos y algas en entornos húmedos (baños, cocinas, lavaderos)',
  },
  {
    codigo: 'B',
    label: 'Paneles de madera (tableros de partículas, fibras, MDF, OSB, madera-cemento, contrachapados, madera maciza, acústicos)',
    criterio_comportamiento: 'Opción 1: Formaldehído clase E1 · Opción 2: nivel formaldehído ≤ 0,1 mg/m³',
    normas_comportamiento: [
      'Formaldehído clase E1 (Opción 1)',
      'Nivel de formaldehído ≤ 0,1 mg/m³ (Opción 2)',
    ],
    normas_ensayo: [
      'UNE-EN 717-1:2006 · Emisión de formaldehído por el método de la cámara (Opción 1)',
      'UNE-EN ISO 16000-9:2006 · Emisión de COV de productos de construcción · método del ensayo en cámara (Opción 2)',
      'Método CDPH-01350 California Department of Public Health (equivalente Opción 2)',
    ],
    confirmacion_fabricante: 'Ausencia de conservantes regulados de la madera',
  },
  {
    codigo: 'C',
    label: 'Estructuras de madera (madera laminada encolada)',
    criterio_comportamiento: 'Igual que categoría B',
    normas_comportamiento: ['Igual que categoría B'],
    normas_ensayo: ['Igual que categoría B'],
    confirmacion_fabricante: 'Igual que categoría B',
    _noAplicaDefault: true,
  },
  {
    codigo: 'D',
    label: 'Revestimientos de suelo de madera (parqué)',
    criterio_comportamiento: 'Igual que categoría B',
    normas_comportamiento: ['Igual que categoría B'],
    normas_ensayo: ['Igual que categoría B'],
    confirmacion_fabricante: 'Igual que categoría B',
    _noAplicaDefault: true,
  },
  {
    codigo: 'E',
    label: 'Revestimientos de suelo resilientes, textiles y laminados (vinilo, linóleo, corcho, caucho, moqueta, madera laminada)',
    criterio_comportamiento: 'Igual que categoría B',
    normas_comportamiento: ['Igual que categoría B'],
    normas_ensayo: ['Igual que categoría B'],
    confirmacion_fabricante: 'Igual que categoría B',
  },
  {
    codigo: 'F',
    label: 'Paneles para falso techo',
    criterio_comportamiento: 'Igual que categoría B',
    normas_comportamiento: ['Igual que categoría B'],
    normas_ensayo: ['Igual que categoría B'],
    confirmacion_fabricante: 'Igual que categoría B',
  },
  {
    codigo: 'G',
    label: 'Adhesivos de revestimientos de suelo',
    criterio_comportamiento: 'Ausencia de sustancias cancerígenas o sensibilizantes (SAM) · clasificaciones C1/C2/C3 del Anexo A UNE-EN 13999-1',
    normas_comportamiento: [
      'Sistema Armonizado Mundial (SAM) de clasificación y etiquetado de productos químicos',
      'Clasificaciones C1, C2 y C3 · Anexo A UNE-EN 13999-1:2007 · Procedimiento general',
    ],
    normas_ensayo: [
      'UNE-EN 13999-2:2014 · Determinación de COV',
      'UNE-EN 13999-3:2007+A1:2009 · Determinación de aldehídos volátiles',
      'UNE-EN 13999-4:2007+A1:2009 · Determinación de diisocianatos volátiles',
    ],
    confirmacion_fabricante: 'No aplicable',
  },
  {
    codigo: 'H',
    label: 'Revestimientos de pared',
    criterio_comportamiento: 'Contenido en monómero de cloruro de vinilo (MCV) · nivel de formaldehído · migración de metales pesados',
    normas_comportamiento: [
      'UNE-EN 233:2000 sección 5.7 · Papeles pintados acabados / Revestimientos de pared vinílicos y plásticos',
      'UNE 57162/1M:1997 · Revestimientos decorativos en rollos · Especificación de revestimientos para decoración posterior',
      'UNE-EN 259-1:2002 sección 4.5-4.7 · Revestimientos de pared de alta resistencia',
    ],
    normas_ensayo: [
      'UNE-EN 12149:1998 · Migración de metales pesados y otros elementos extraíbles, contenido MCV y liberación de formaldehído',
    ],
    confirmacion_fabricante: 'No aplicable',
  },
]

// 3) Medidas SMACNA de gestión de obra (pliego LT02 MAD2 · basado en LEED)
// OPCIONAL · solo se activan si el pliego del proyecto las exige.
const PPCAI_SMACNA_APARTADOS = [
  {
    codigo: '1',
    titulo: 'Protección del sistema y los equipos de climatización y ventilación (HVAC)',
    medidas: [
      'Sellado de conductos HVAC durante la construcción con film plástico o tapones específicos',
      'Filtros mínimo clase F7 según EN 779:2012 (equivalente ISO 16890 ePM1 > 50 %) en tomas de aire durante la ejecución',
      'Prohibición de funcionamiento del HVAC durante trabajos de acabado con productos emisivos',
      'Limpieza de conductos antes de la entrega si estuvieron operativos durante la obra',
    ],
  },
  {
    codigo: '2',
    titulo: 'Control de las fuentes emisoras',
    medidas: [
      'Verificación de fichas técnicas antes de la compra',
      'Almacenamiento fuera del edificio en zonas ventiladas',
      'Uso preferente de productos con etiqueta EMICODE EC1, Blauer Engel, GreenGuard o equivalente',
    ],
  },
  {
    codigo: '3',
    titulo: 'Interrupción de las rutas de los contaminantes',
    medidas: [
      'Sectorización física con barreras temporales durante trabajos emisivos',
      'Ventilación forzada localizada en las zonas de aplicación',
      'Cierre de zonas terminadas para evitar recontaminación',
    ],
  },
  {
    codigo: '4',
    titulo: 'Limpieza y mantenimiento',
    medidas: [
      'Aspirado HEPA de todas las superficies antes de la entrega',
      'Retirada diaria de residuos y polvo de las zonas de trabajo',
      'Control de humedad para prevenir crecimiento de hongos',
    ],
  },
  {
    codigo: '5',
    titulo: 'Materiales absorbentes',
    medidas: [
      'Protección de materiales absorbentes frente a humedad y polvo',
      'Instalación DESPUÉS de los trabajos con disolventes',
      'Verificación del secado del soporte antes de instalar acabados',
    ],
  },
  {
    codigo: '6',
    titulo: 'Programación y cronograma de las obras',
    medidas: [
      'Aplicación de productos con disolventes en horarios sin presencia de otros oficios',
      'Purga de ventilación mínima 72 h tras aplicaciones',
      'Instalación de absorbentes al final del ciclo constructivo',
    ],
  },
]

// Zonas típicas de "ocupación elevada e impredecible" del manual (NA7)
// donde el punto 4 del criterio 1 exige sensores de CO2
const PPCAI_ZONAS_OCUPACION_ELEVADA = [
  'Auditorios', 'Gimnasios', 'Comercios', 'Salas de espera', 'Cines', 'Aulas', 'Salas de reuniones grandes',
]

// Estado inicial del contenido_json del PPCAI cuando se crea un nuevo plan
// Estructura fiel al Manual BREEAM ES NC 2015 · SyB 2 (7 secciones)
function ppcaiVacio() {
  return {
    // Sección 1 · Datos generales
    objetivo: 'Prevenir y controlar la contaminación del aire interior durante la construcción y verificar los niveles al finalizar la obra, para cumplir los requisitos del criterio SyB 2 del Manual BREEAM ES Nueva Construcción 2015 (edición 02 · IPC-BREEAM-01-12).',
    ambito: '',
    cronograma: '',
    puntos_objetivo: 1,   // 1, 2 o 3 · puntos del criterio SyB 2 pretendidos

    // Sección 2 · Los 5 elementos oficiales del PPCAI (Manual · criterio 1 a-e)
    elementos_manual: PPCAI_ELEMENTOS_MANUAL.map(el => ({
      codigo: el.codigo,
      titulo: el.titulo,
      medidas_seleccionadas: [],   // índices de el.medidas
      medidas_adicionales: '',
      responsable: '',
      frecuencia_control: '',
    })),

    // Sección 3 · Criterios adicionales del punto 1 del manual
    criterios_adicionales: {
      distancias_hvac: {
        aplica: false,               // solo edificios con aire acondicionado
        tomas_salidas_10m: false,    // >10 m entre tomas y salidas de aire
        tomas_fuentes_20m: false,    // >20 m entre tomas y fuentes contaminación exterior
        justificacion: '',
      },
      diseno_ventilacion: {
        cumple_une13779: false,
        caudales_verificados: '',
        referencia_calculo: '',
      },
      sensores_co2: {
        aplica: false,               // zonas de ocupación elevada e impredecible
        zonas: [],                   // subset de PPCAI_ZONAS_OCUPACION_ELEVADA
        conectado_ventilacion: false,
        justificacion: '',
      },
    },

    // Sección 4 · Tabla 13 · productos por categoría (A-H)
    productos_por_categoria: PPCAI_CATEGORIAS_PRODUCTOS.map(c => ({
      codigo: c.codigo,
      label: c.label,
      criterio_comportamiento: c.criterio_comportamiento,
      normas_comportamiento: c.normas_comportamiento,
      normas_ensayo: c.normas_ensayo,
      confirmacion_fabricante: c.confirmacion_fabricante,
      aplica: !c._noAplicaDefault,
      productos: [],
      notas: '',
    })),

    // Sección 5 · Compromiso post-construcción (punto 2 y 3 del criterio)
    post_construccion: {
      punto_2_productos: false,           // 2 puntos · cumple Tabla 13 en todas las categorías
      punto_3a_covt: false,               // 3 puntos · medición COVT < 300 μg/m³ · 8 h
      punto_3b_formaldehido: false,       // 3 puntos · medición formaldehído ≤ 100 μg/m³ · 30 min
      ejemplar_covt: false,               // Nivel ejemplar · COVT ≤ 60 μg/m³
      ejemplar_formaldehido: false,       // Nivel ejemplar · formaldehído ≤ 10 μg/m³
      metodos_ensayo: [
        'UNE-ISO 16000-4:2006 · Formaldehído · muestreo difusivo',
        'UNE-ISO 16000-6:2006 · COVT · muestreo activo cámara adsorbente',
        'UNE-EN ISO 16017-2:2004 · COVT · muestreo difusivo cámara adsorbente',
        'UNE 77260-3:2004 · Formaldehído · método alternativo',
      ],
      laboratorio: '',
      compromiso_reduccion: 'Si los niveles superan los límites, el equipo de proyecto se compromete a aplicar las medidas correctoras recogidas en el PPCAI hasta cumplir los umbrales.',
    },

    // Sección 6 · Medidas SMACNA del pliego (opcional · solo si el pliego las exige)
    aplica_smacna: false,
    medidas_smacna: PPCAI_SMACNA_APARTADOS.map(a => ({
      codigo: a.codigo,
      titulo: a.titulo,
      medidas_seleccionadas: [],
      medidas_adicionales: '',
    })),

    // Sección 7 · Notas del asesor
    notas_asesor: '',
  }
}

// ── Sub-componentes helpers ─────────────────────────────────────────────
function Field({ label, children, hint, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}`, minWidth: 0 }}>
      <label style={LABEL_STYLE}>{label}</label>
      {children}
      {hint && (
        <div style={{ fontSize: '0.7rem', color: C.muted, marginTop: '0.25rem', fontStyle: 'italic', fontFamily: FF }}>
          {hint}
        </div>
      )}
    </div>
  )
}

function TextInput({ value, onChange, type = 'text', placeholder }) {
  return (
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} style={INPUT_STYLE} />
  )
}

function Textarea({ value, onChange, rows = 3, placeholder }) {
  return (
    <textarea value={value || ''} onChange={e => onChange(e.target.value)}
      rows={rows} placeholder={placeholder}
      style={{ ...INPUT_STYLE, resize: 'vertical', fontFamily: FF }} />
  )
}

function SelectInput({ value, onChange, options }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={INPUT_STYLE}>
      {options.map(o => (
        <option key={o.code || o} value={o.code || o}>{o.label || o}</option>
      ))}
    </select>
  )
}

function Button({ children, onClick, variant = 'primary', icon: Icon, disabled = false, small = false }) {
  const bg = variant === 'primary' ? C.accent : variant === 'danger' ? C.warn : variant === 'olive' ? C.olive : 'white'
  const fg = variant === 'ghost' ? C.text : 'white'
  const border = variant === 'ghost' ? `1px solid ${C.border}` : 'none'
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        padding: small ? '0.35rem 0.75rem' : '0.55rem 1rem',
        fontSize: small ? '0.78rem' : '0.86rem',
        background: bg, color: fg, border, borderRadius: '4px',
        fontFamily: FF, fontWeight: '600', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
      }}>
      {Icon && <Icon size={small ? 14 : 16} />}
      {children}
    </button>
  )
}

function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '2rem 1rem', color: C.muted, fontFamily: FF }}>
      {Icon && <Icon size={40} color={C.border} style={{ marginBottom: '0.5rem' }} />}
      <div style={{ fontSize: '0.9rem', fontWeight: '600', color: C.text, marginBottom: '0.25rem' }}>{title}</div>
      {hint && <div style={{ fontSize: '0.8rem', marginBottom: '0.75rem' }}>{hint}</div>}
      {action}
    </div>
  )
}

function Card({ title, icon: Icon, children, action }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '6px', marginBottom: '1rem' }}>
      <div style={{ padding: '0.875rem 1rem', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {Icon && <Icon size={16} color={C.accent} />}
        <span style={{ fontFamily: FF, fontWeight: '600', color: C.text, fontSize: '0.92rem', flex: 1 }}>
          {title}
        </span>
        {action}
      </div>
      <div style={{ padding: '1rem' }}>{children}</div>
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════
//   COMPONENTE PRINCIPAL · Tab «Planes iniciales» del Cockpit
// ═══════════════════════════════════════════════════════════════════════
export default function PlanesIniciales({ expedienteId, projectId, projectName, userId, contratistaPrincipal, documentos }) {
  const [planes, setPlanes] = useState([])
  const [loading, setLoading] = useState(true)
  const [editandoPlan, setEditandoPlan] = useState(null)   // objeto plan completo o null

  const cargar = useCallback(async () => {
    if (!expedienteId) { setLoading(false); return }
    setLoading(true)
    const { data } = await supabase.from('cc_planes_iniciales')
      .select('*').eq('expediente_id', expedienteId)
      .order('created_at', { ascending: true })
    setPlanes(data || [])
    setLoading(false)
  }, [expedienteId])

  useEffect(() => { cargar() }, [cargar])

  const crearPlan = async (tipo) => {
    if (!userId || !expedienteId || !projectId) return
    const tipoDef = TIPO_BY_CODE[tipo]
    if (!tipoDef) return
    const payload = {
      expediente_id: expedienteId,
      project_id: projectId,
      user_id: userId,
      tipo,
      titulo: tipoDef.label,
      estado: 'borrador',
      version: 1,
      criterios_breeam_vinculados: tipoDef.criterios,
      contenido_json: tipo === 'ppcai' ? ppcaiVacio() : {},
    }
    const { data, error } = await supabase.from('cc_planes_iniciales')
      .insert(payload).select().maybeSingle()
    if (error) { alert(`Error creando el plan · ${error.message}`); return }
    setPlanes(ps => [...ps, data])
    setEditandoPlan(data)
  }

  const guardarPlan = async (plan) => {
    const { data, error } = await supabase.from('cc_planes_iniciales')
      .update({
        contenido_json: plan.contenido_json,
        titulo: plan.titulo,
        estado: plan.estado,
        criterios_breeam_vinculados: plan.criterios_breeam_vinculados,
        firmante_nombre: plan.firmante_nombre,
        firmante_cargo: plan.firmante_cargo,
        firmante_email: plan.firmante_email,
        firma_fecha: plan.firma_fecha,
      })
      .eq('id', plan.id).select().maybeSingle()
    if (error) { alert(`Error al guardar · ${error.message}`); return null }
    setPlanes(ps => ps.map(p => p.id === plan.id ? data : p))
    return data
  }

  const eliminarPlan = async (planId) => {
    if (!window.confirm('¿Eliminar el plan? Esta acción no se puede deshacer.')) return
    const { error } = await supabase.from('cc_planes_iniciales').delete().eq('id', planId)
    if (error) { alert(`Error · ${error.message}`); return }
    setPlanes(ps => ps.filter(p => p.id !== planId))
    if (editandoPlan?.id === planId) setEditandoPlan(null)
  }

  if (!expedienteId) {
    return (
      <Card title="Planes iniciales requeridos por BREEAM" icon={ClipboardText}>
        <EmptyState icon={ClipboardText} title="Configura primero el expediente"
          hint="Los planes iniciales se asocian al expediente. Créalo en la pestaña Setup." />
      </Card>
    )
  }

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: C.muted, fontFamily: FF }}>Cargando planes...</div>
  }

  // Vista de edición
  if (editandoPlan) {
    return (
      <EditorPlan
        plan={editandoPlan}
        projectName={projectName}
        contratistaPrincipal={contratistaPrincipal}
        documentos={documentos}
        onCancel={() => { setEditandoPlan(null); cargar() }}
        onSave={async (p) => {
          const saved = await guardarPlan(p)
          if (saved) setEditandoPlan(saved)
        }}
      />
    )
  }

  // Listado de planes
  return (
    <>
      <Card title="Planes iniciales del expediente" icon={ClipboardText}>
        <div style={{ display: 'grid', gap: '0.6rem' }}>
          {TIPOS_PLAN.map(tipoDef => {
            const planExistente = planes.find(p => p.tipo === tipoDef.code)
            return (
              <PlanTipoRow
                key={tipoDef.code}
                tipoDef={tipoDef}
                plan={planExistente}
                onCrear={() => crearPlan(tipoDef.code)}
                onEditar={() => setEditandoPlan(planExistente)}
                onEliminar={() => eliminarPlan(planExistente.id)}
              />
            )
          })}
        </div>
      </Card>
    </>
  )
}

function PlanTipoRow({ tipoDef, plan, onCrear, onEditar, onEliminar }) {
  const Icon = tipoDef.icon
  const estado = plan ? ESTADO_LABELS[plan.estado] : null

  return (
    <div style={{
      padding: '0.85rem',
      border: `1px solid ${plan ? (plan.estado === 'final' ? C.olive : C.border) : C.border}`,
      borderRadius: '5px', background: '#FDFCF9',
      opacity: tipoDef.disponible || plan ? 1 : 0.55,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <Icon size={22} color={C.accent} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FF, fontWeight: '600', color: C.text, fontSize: '0.9rem' }}>
            {tipoDef.label}
            <span style={{ fontSize: '0.72rem', color: C.muted, fontWeight: '400', marginLeft: '0.5rem' }}>
              · {tipoDef.criterios.join(' · ')}
            </span>
          </div>
          <div style={{ fontSize: '0.75rem', color: C.muted, fontFamily: FF, marginTop: '0.2rem' }}>
            {plan
              ? `v${plan.version} · actualizado ${new Date(plan.updated_at).toLocaleDateString('es-ES')}`
              : tipoDef.descripcion}
          </div>
        </div>
        {plan && estado && (
          <div style={{
            padding: '0.28rem 0.65rem', background: estado.bg, color: estado.color,
            borderRadius: '3px', fontSize: '0.72rem', fontWeight: '600', fontFamily: FF, whiteSpace: 'nowrap',
          }}>
            {estado.label}
          </div>
        )}
        {plan ? (
          <>
            <Button variant="ghost" small onClick={onEditar}>Ver / Editar</Button>
            <Button variant="ghost" small icon={TrashSimple} onClick={onEliminar}>Eliminar</Button>
          </>
        ) : tipoDef.disponible ? (
          <Button variant="olive" small icon={Plus} onClick={onCrear}>Crear plan</Button>
        ) : (
          <span style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FF, fontStyle: 'italic' }}>
            Próxima sesión
          </span>
        )}
      </div>
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════
//   EDITOR DE PLAN · router por tipo
// ═══════════════════════════════════════════════════════════════════════
function EditorPlan({ plan, projectName, contratistaPrincipal, documentos, onSave, onCancel }) {
  const [local, setLocal] = useState(plan)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [generandoIA, setGenerandoIA] = useState(false)
  const [exportando, setExportando] = useState(false)

  useEffect(() => { setLocal(plan) }, [plan])

  const setField = (path, value) => {
    setDirty(true)
    setLocal(prev => {
      const next = { ...prev }
      if (path.length === 1) { next[path[0]] = value }
      else {
        // path como ['contenido_json', 'elementos_manual', 0, 'responsable']
        const clone = JSON.parse(JSON.stringify(next))
        let cur = clone
        for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]]
        cur[path[path.length - 1]] = value
        return clone
      }
      return next
    })
  }

  const guardar = async () => {
    setSaving(true)
    try { await onSave(local); setDirty(false) } finally { setSaving(false) }
  }

  const generarConIA = async () => {
    if (!window.confirm('¿Generar borrador automático con la IA a partir de los documentos ya analizados? Esta acción sobrescribirá el contenido actual del plan.')) return
    setGenerandoIA(true)
    try {
      const contenido = await generarBorradorPPCAI(plan, contratistaPrincipal, documentos)
      setLocal(prev => ({ ...prev, contenido_json: contenido }))
      setDirty(true)
    } catch (e) {
      alert(`Error al generar borrador · ${e.message}`)
    } finally {
      setGenerandoIA(false)
    }
  }

  const exportarDocx = async () => {
    if (dirty && !window.confirm('Hay cambios sin guardar en el editor · el docx se generará con la versión guardada en la base de datos. ¿Continúas de todos modos?')) return
    setExportando(true)
    try {
      if (plan.tipo === 'ppcai') {
        await exportPpcaiToWord({
          plan: local,
          projectName,
          contratistaPrincipal,
          ELEMENTOS_MANUAL: PPCAI_ELEMENTOS_MANUAL,
          CATEGORIAS_PRODUCTOS: PPCAI_CATEGORIAS_PRODUCTOS,
          SMACNA_APARTADOS: PPCAI_SMACNA_APARTADOS,
        })
      } else {
        alert('La exportación docx para este tipo de plan aún no está implementada.')
      }
    } catch (e) {
      alert(`Error al exportar docx · ${e.message}`)
    } finally { setExportando(false) }
  }

  const exportarPdf = async () => {
    if (dirty && !window.confirm('Hay cambios sin guardar en el editor · el PDF se generará con la versión guardada en la base de datos. ¿Continúas de todos modos?')) return
    setExportando(true)
    try {
      if (plan.tipo === 'ppcai') {
        await exportPpcaiToPdf({
          plan: local,
          projectName,
          contratistaPrincipal,
          ELEMENTOS_MANUAL: PPCAI_ELEMENTOS_MANUAL,
          CATEGORIAS_PRODUCTOS: PPCAI_CATEGORIAS_PRODUCTOS,
          SMACNA_APARTADOS: PPCAI_SMACNA_APARTADOS,
        })
      } else {
        alert('La exportación PDF para este tipo de plan aún no está implementada.')
      }
    } catch (e) {
      alert(`Error al exportar PDF · ${e.message}`)
    } finally { setExportando(false) }
  }

  const tipoDef = TIPO_BY_CODE[plan.tipo]

  return (
    <div>
      {/* Cabecera del editor */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        marginBottom: '1rem', paddingBottom: '0.75rem',
        borderBottom: `1px solid ${C.border}`,
      }}>
        {tipoDef?.icon && <tipoDef.icon size={22} color={C.accent} />}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '1rem', fontWeight: '700', color: C.text, fontFamily: FF }}>
            {plan.titulo}
          </div>
          <div style={{ fontSize: '0.76rem', color: C.muted, fontFamily: FF }}>
            {projectName} · Criterio {tipoDef?.criterios.join(' · ')} · v{plan.version}
          </div>
        </div>
        <Button variant="ghost" small onClick={onCancel}>← Volver al listado</Button>
      </div>

      {/* Barra de acciones */}
      <div style={{
        padding: '0.6rem 0.75rem',
        background: '#EBF3F8', border: `1px solid ${C.accent}`,
        borderRadius: '5px', marginBottom: '1rem',
        display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
      }}>
        <Sparkle size={16} color={C.accent} />
        <span style={{ fontSize: '0.82rem', color: C.text, fontFamily: FF, flex: 1 }}>
          Puedes generar automáticamente un borrador del plan a partir de los documentos ya analizados en Fase 2.
        </span>
        <Button variant="olive" small icon={generandoIA ? ArrowClockwise : Sparkle}
          onClick={generarConIA} disabled={generandoIA}>
          {generandoIA ? 'Generando…' : 'Generar borrador con IA'}
        </Button>
      </div>

      {/* Editor específico por tipo · por ahora solo PPCAI */}
      {plan.tipo === 'ppcai' ? (
        <PpcaiEditor local={local} setField={setField} contratistaPrincipal={contratistaPrincipal} />
      ) : (
        <EmptyState icon={ClipboardText} title="Editor no disponible"
          hint={`El editor para ${tipoDef?.label} se implementará en próximas sesiones.`} />
      )}

      {/* Barra inferior · guardar + exportar */}
      <div style={{
        position: 'sticky', bottom: 0, background: C.bg,
        padding: '0.75rem 0', marginTop: '1.5rem',
        borderTop: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', gap: '0.5rem',
      }}>
        <div style={{ flex: 1, fontSize: '0.78rem', color: dirty ? C.warn : C.muted, fontFamily: FF }}>
          {dirty ? '● Cambios sin guardar' : 'Todos los cambios guardados'}
        </div>
        <Button variant="ghost" small icon={FilePdf} onClick={exportarPdf} disabled={exportando}>
          Exportar PDF
        </Button>
        <Button variant="ghost" small icon={DownloadSimple} onClick={exportarDocx} disabled={exportando}>
          Exportar docx
        </Button>
        <Button icon={FloppyDisk} onClick={guardar} disabled={saving || !dirty}>
          {saving ? 'Guardando…' : 'Guardar plan'}
        </Button>
      </div>
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════
//   EDITOR ESPECÍFICO · PPCAI
//   Estructura fiel al Manual BREEAM ES NC 2015 (SyB 2 · páginas 70-81)
// ═══════════════════════════════════════════════════════════════════════
function PpcaiEditor({ local, setField, contratistaPrincipal }) {
  const cj = local.contenido_json || {}
  const ca = cj.criterios_adicionales || {}
  const pc = cj.post_construccion || {}

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>

      {/* Aviso encabezado · fuente normativa */}
      <div style={{
        padding: '0.7rem 0.9rem',
        background: '#FBF8F2', border: `1px solid ${C.amber}`,
        borderRadius: '4px', fontSize: '0.78rem', color: C.text, fontFamily: FF,
      }}>
        <strong>Fuente normativa:</strong> este editor sigue la estructura oficial del Manual Técnico BREEAM ES Nueva Construcción 2015
        (IPC-BREEAM-01-12 · ED.02 · 31/03/2017), páginas 70-81 · criterio SyB 2. Las medidas SMACNA de la sección 6 son un anexo opcional que solo se activa si el pliego del proyecto las exige.
      </div>

      {/* ─── 1 · Datos generales del plan ───────────────────────────────────── */}
      <Card title="1 · Datos generales del plan" icon={ClipboardText}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <Field label="Estado" span={1}>
            <SelectInput value={local.estado}
              onChange={v => setField(['estado'], v)}
              options={[
                { code: 'borrador', label: 'Borrador' },
                { code: 'revision', label: 'En revisión con DF / asesor' },
                { code: 'final',    label: 'Final · firmado' },
              ]} />
          </Field>
          <Field label="Contratista principal" span={1}
            hint="Se rellena desde la pestaña Contratistas del Cockpit.">
            <TextInput value={contratistaPrincipal?.nombre || 'No asignado'} onChange={() => {}} placeholder="—" />
          </Field>
          <Field label="Puntos del criterio SyB 2 que el proyecto pretende obtener" span={2}
            hint="1 punto = PPCAI implementado · 2 puntos = 1 + cumplimiento de la Tabla 13 productos · 3 puntos = 2 + medición post-construcción (formaldehído + COVT). Nivel ejemplar disponible con umbrales más exigentes.">
            <SelectInput value={cj.puntos_objetivo || 1}
              onChange={v => setField(['contenido_json', 'puntos_objetivo'], parseInt(v))}
              options={[
                { code: 1, label: '1 punto · solo el PPCAI implementado' },
                { code: 2, label: '2 puntos · PPCAI + cumplimiento Tabla 13 productos' },
                { code: 3, label: '3 puntos · PPCAI + Tabla 13 + medición post-construcción' },
              ]} />
          </Field>
          <Field label="Objetivo del plan" span={2}>
            <Textarea value={cj.objetivo} rows={3}
              onChange={v => setField(['contenido_json', 'objetivo'], v)} />
          </Field>
          <Field label="Ámbito de aplicación" span={2}
            hint="A qué zonas del edificio y a qué fases de la obra aplica el plan.">
            <Textarea value={cj.ambito} rows={2}
              onChange={v => setField(['contenido_json', 'ambito'], v)}
              placeholder="Ej. Todas las zonas habitables y ocupables del edificio durante las fases de acabados, instalaciones y puesta en servicio (meses 12-24 de la obra)." />
          </Field>
          <Field label="Cronograma general de implementación" span={2}
            hint="Cuándo entra en vigor el plan y hasta cuándo se mantiene.">
            <Textarea value={cj.cronograma} rows={2}
              onChange={v => setField(['contenido_json', 'cronograma'], v)}
              placeholder="Ej. El plan aplica desde el inicio de trabajos interiores hasta la entrega del edificio. Revisión mensual del responsable BREEAM del contratista." />
          </Field>
        </div>
      </Card>

      {/* ─── 2 · Los 5 elementos oficiales del PPCAI (Manual · criterio 1 a-e) ── */}
      <div style={{
        padding: '0.55rem 0.85rem', background: '#EBF3F8', border: `1px solid ${C.accent}`,
        borderRadius: '4px', fontSize: '0.8rem', color: C.text, fontFamily: FF,
      }}>
        <strong>2 · Los cinco elementos del PPCAI (Manual BREEAM ES · criterio 1)</strong>
        <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '0.15rem' }}>
          Para obtener el <strong>primer punto</strong> del criterio SyB 2, el pliego oficial del manual exige que el PPCAI aborde estos cinco elementos (a-e). Marca las medidas aplicables en cada uno.
        </div>
      </div>
      {(cj.elementos_manual || []).map((el, i) => {
        const elDef = PPCAI_ELEMENTOS_MANUAL[i]
        return (
          <Card key={el.codigo} title={`2.${el.codigo} · ${elDef?.titulo || el.titulo}`} icon={ClipboardText}>
            <div style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FF, marginBottom: '0.55rem', fontStyle: 'italic' }}>
              {elDef?.referencia_manual}
            </div>
            <div style={{ fontSize: '0.78rem', color: C.text, fontFamily: FF, marginBottom: '0.6rem' }}>
              Marca las medidas que el contratista se compromete a aplicar.
            </div>
            <div style={{ display: 'grid', gap: '0.35rem', marginBottom: '0.75rem' }}>
              {(elDef?.medidas || []).map((med, mi) => {
                const marcada = (el.medidas_seleccionadas || []).includes(mi)
                return (
                  <label key={mi} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                    padding: '0.4rem 0.55rem', border: `1px solid ${marcada ? C.olive : C.border}`,
                    borderRadius: '4px', background: marcada ? '#F5F8ED' : 'white',
                    cursor: 'pointer', fontFamily: FF,
                  }}>
                    <input type="checkbox" checked={marcada}
                      onChange={() => {
                        const cur = el.medidas_seleccionadas || []
                        const next = marcada ? cur.filter(x => x !== mi) : [...cur, mi].sort((a, b) => a - b)
                        setField(['contenido_json', 'elementos_manual', i, 'medidas_seleccionadas'], next)
                      }}
                      style={{ marginTop: '0.2rem' }} />
                    <span style={{ fontSize: '0.82rem', color: C.text, lineHeight: 1.4 }}>{med}</span>
                  </label>
                )
              })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <Field label="Responsable de la aplicación">
                <TextInput value={el.responsable}
                  onChange={v => setField(['contenido_json', 'elementos_manual', i, 'responsable'], v)}
                  placeholder="Ej. Jefe de obra, encargado de acabados, subcontrata X..." />
              </Field>
              <Field label="Frecuencia de control">
                <SelectInput value={el.frecuencia_control}
                  onChange={v => setField(['contenido_json', 'elementos_manual', i, 'frecuencia_control'], v)}
                  options={[
                    { code: '', label: '— Sin definir —' },
                    { code: 'diaria', label: 'Diaria' },
                    { code: 'semanal', label: 'Semanal' },
                    { code: 'quincenal', label: 'Quincenal' },
                    { code: 'mensual', label: 'Mensual' },
                    { code: 'por_hito', label: 'Por hito constructivo' },
                  ]} />
              </Field>
              <Field label="Medidas adicionales específicas del proyecto" span={2}>
                <Textarea value={el.medidas_adicionales} rows={2}
                  onChange={v => setField(['contenido_json', 'elementos_manual', i, 'medidas_adicionales'], v)}
                  placeholder="Aporta cualquier medida adicional que aplique el contratista y no esté en el listado anterior." />
              </Field>
            </div>
          </Card>
        )
      })}

      {/* ─── 3 · Criterios adicionales del punto 1 (manual) ─────────────────── */}
      <Card title="3 · Criterios adicionales del punto 1 del manual" icon={ClipboardText}>
        <div style={{ fontSize: '0.75rem', color: C.muted, fontFamily: FF, marginBottom: '0.75rem' }}>
          Además de los 5 elementos anteriores, el criterio 1 del manual exige verificar distancias entre tomas y salidas de aire (edificios climatizados), cumplimiento de la UNE-EN 13779:2008 para el diseño de ventilación, y presencia de sensores CO₂ conectados a la ventilación en zonas de ocupación elevada e impredecible.
        </div>

        {/* 3.1 Distancias HVAC */}
        <div style={{ padding: '0.6rem 0.75rem', border: `1px solid ${C.border}`, borderRadius: '4px', marginBottom: '0.6rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: FF, fontSize: '0.86rem', color: C.text, fontWeight: '600', marginBottom: '0.4rem' }}>
            <input type="checkbox" checked={!!ca.distancias_hvac?.aplica}
              onChange={e => setField(['contenido_json', 'criterios_adicionales', 'distancias_hvac', 'aplica'], e.target.checked)} />
            3.1 · Distancias mínimas en el sistema HVAC (solo edificios con aire acondicionado)
          </label>
          {ca.distancias_hvac?.aplica && (
            <div style={{ display: 'grid', gap: '0.4rem', paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', fontFamily: FF, color: C.text }}>
                <input type="checkbox" checked={!!ca.distancias_hvac?.tomas_salidas_10m}
                  onChange={e => setField(['contenido_json', 'criterios_adicionales', 'distancias_hvac', 'tomas_salidas_10m'], e.target.checked)} />
                <span>Distancia &gt; 10 m entre las tomas de aire exterior y las salidas de aire viciado del edificio</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', fontFamily: FF, color: C.text }}>
                <input type="checkbox" checked={!!ca.distancias_hvac?.tomas_fuentes_20m}
                  onChange={e => setField(['contenido_json', 'criterios_adicionales', 'distancias_hvac', 'tomas_fuentes_20m'], e.target.checked)} />
                <span>Distancia &gt; 20 m entre las tomas de aire exterior y las fuentes de contaminación externas (tráfico, aparcamientos, chimeneas de cocina, torres de refrigeración)</span>
              </label>
              <Field label="Justificación · plano y memoria de justificación">
                <Textarea rows={2} value={ca.distancias_hvac?.justificacion}
                  onChange={v => setField(['contenido_json', 'criterios_adicionales', 'distancias_hvac', 'justificacion'], v)}
                  placeholder="Ej. plano de cubiertas M1191-INS-AC-05 · toma en cubierta noreste a 24 m del extractor de cocina y 32 m del acceso a garaje." />
              </Field>
            </div>
          )}
        </div>

        {/* 3.2 Diseño de ventilación · UNE-EN 13779 */}
        <div style={{ padding: '0.6rem 0.75rem', border: `1px solid ${C.border}`, borderRadius: '4px', marginBottom: '0.6rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: FF, fontSize: '0.86rem', color: C.text, fontWeight: '600', marginBottom: '0.4rem' }}>
            <input type="checkbox" checked={!!ca.diseno_ventilacion?.cumple_une13779}
              onChange={e => setField(['contenido_json', 'criterios_adicionales', 'diseno_ventilacion', 'cumple_une13779'], e.target.checked)} />
            3.2 · Diseño del sistema de ventilación conforme a UNE-EN 13779:2008
          </label>
          {ca.diseno_ventilacion?.cumple_une13779 && (
            <div style={{ display: 'grid', gap: '0.4rem', paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
              <Field label="Caudales de aire exterior verificados"
                hint="Anota los caudales exigidos por UNE-EN 13779 aplicados en el proyecto (l/s·persona o l/s·m² según el uso).">
                <Textarea rows={2} value={ca.diseno_ventilacion?.caudales_verificados}
                  onChange={v => setField(['contenido_json', 'criterios_adicionales', 'diseno_ventilacion', 'caudales_verificados'], v)}
                  placeholder="Ej. IDA 2 · 12,5 l/s·persona en oficinas · 8 l/s·persona en salas de reuniones." />
              </Field>
              <Field label="Referencia del cálculo (memoria climatización, anexo, plano)">
                <TextInput value={ca.diseno_ventilacion?.referencia_calculo}
                  onChange={v => setField(['contenido_json', 'criterios_adicionales', 'diseno_ventilacion', 'referencia_calculo'], v)}
                  placeholder="Ej. Anexo 3 · Cálculo de ventilación · pp. 12-18 · Ingeniería XX (05/2025)" />
              </Field>
            </div>
          )}
        </div>

        {/* 3.3 Sensores CO2 */}
        <div style={{ padding: '0.6rem 0.75rem', border: `1px solid ${C.border}`, borderRadius: '4px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: FF, fontSize: '0.86rem', color: C.text, fontWeight: '600', marginBottom: '0.4rem' }}>
            <input type="checkbox" checked={!!ca.sensores_co2?.aplica}
              onChange={e => setField(['contenido_json', 'criterios_adicionales', 'sensores_co2', 'aplica'], e.target.checked)} />
            3.3 · Sensores de CO₂ en zonas de ocupación elevada e impredecible
          </label>
          {ca.sensores_co2?.aplica && (
            <div style={{ display: 'grid', gap: '0.4rem', paddingLeft: '1.5rem', marginTop: '0.5rem' }}>
              <Field label="Zonas del edificio equipadas"
                hint="Selecciona todas las que apliquen. Categorías típicas de zonas NA7 del manual.">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.2rem' }}>
                  {PPCAI_ZONAS_OCUPACION_ELEVADA.map(z => {
                    const marcada = (ca.sensores_co2?.zonas || []).includes(z)
                    return (
                      <label key={z} style={{
                        display: 'flex', alignItems: 'center', gap: '0.3rem',
                        padding: '0.25rem 0.55rem',
                        border: `1px solid ${marcada ? C.olive : C.border}`,
                        borderRadius: '3px',
                        background: marcada ? '#F5F8ED' : 'white',
                        fontSize: '0.78rem', fontFamily: FF, color: C.text, cursor: 'pointer',
                      }}>
                        <input type="checkbox" checked={marcada}
                          onChange={() => {
                            const cur = ca.sensores_co2?.zonas || []
                            const next = marcada ? cur.filter(x => x !== z) : [...cur, z]
                            setField(['contenido_json', 'criterios_adicionales', 'sensores_co2', 'zonas'], next)
                          }} />
                        {z}
                      </label>
                    )
                  })}
                </div>
              </Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', fontFamily: FF, color: C.text }}>
                <input type="checkbox" checked={!!ca.sensores_co2?.conectado_ventilacion}
                  onChange={e => setField(['contenido_json', 'criterios_adicionales', 'sensores_co2', 'conectado_ventilacion'], e.target.checked)} />
                <span>Los sensores están conectados al sistema de ventilación (regulación automática del caudal)</span>
              </label>
              <Field label="Justificación · ubicación y trazabilidad del control">
                <Textarea rows={2} value={ca.sensores_co2?.justificacion}
                  onChange={v => setField(['contenido_json', 'criterios_adicionales', 'sensores_co2', 'justificacion'], v)}
                  placeholder="Ej. 6 sensores E+E EE894 en salas de reuniones (planta 3), integrados con el BMS Loxone que ajusta el caudal de la UTA-3 entre 40 % y 100 %." />
              </Field>
            </div>
          )}
        </div>
      </Card>

      {/* ─── 4 · Tabla 13 · productos por categoría (A-H) ───────────────────── */}
      <Card title="4 · Tabla 13 · Productos con requisitos de COV / formaldehído" icon={ClipboardText}>
        <div style={{ fontSize: '0.78rem', color: C.muted, fontFamily: FF, marginBottom: '0.75rem' }}>
          Las 8 categorías A-H reproducidas literales del Manual BREEAM ES NC 2015 (páginas 77-79). Para obtener el <strong>2.º punto</strong> del criterio SyB 2, todos los productos instalados de las categorías aplicables deben cumplir estos requisitos. Marca las que aplican al proyecto y anota los productos concretos con sus fichas técnicas.
        </div>
        {(cj.productos_por_categoria || []).map((cat, i) => (
          <div key={cat.codigo} style={{
            padding: '0.7rem 0.85rem', marginBottom: '0.5rem',
            border: `1px solid ${cat.aplica ? C.border : '#EEE9DE'}`,
            borderRadius: '4px', background: cat.aplica ? 'white' : '#FBF8F2',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: cat.aplica ? '0.5rem' : 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontFamily: FF, fontSize: '0.85rem', color: C.text, fontWeight: '600' }}>
                <input type="checkbox" checked={cat.aplica}
                  onChange={e => setField(['contenido_json', 'productos_por_categoria', i, 'aplica'], e.target.checked)} />
                <span style={{ padding: '0.15rem 0.4rem', background: C.accent, color: 'white', borderRadius: '2px', fontSize: '0.72rem' }}>
                  {cat.codigo}
                </span>
                {cat.label}
              </label>
            </div>
            {cat.aplica && (
              <>
                <div style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FF, marginBottom: '0.5rem', lineHeight: 1.55 }}>
                  <div style={{ marginBottom: '0.15rem' }}><strong>Criterio de comportamiento:</strong> {cat.criterio_comportamiento}</div>
                  {Array.isArray(cat.normas_comportamiento) && cat.normas_comportamiento.length > 0 && (
                    <div style={{ marginBottom: '0.15rem' }}>
                      <strong>Normas de comportamiento:</strong>
                      <ul style={{ margin: '0.15rem 0 0 1.15rem', padding: 0 }}>
                        {cat.normas_comportamiento.map((n, ni) => <li key={ni}>{n}</li>)}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(cat.normas_ensayo) && cat.normas_ensayo.length > 0 && (
                    <div style={{ marginBottom: '0.15rem' }}>
                      <strong>Normas de ensayo:</strong>
                      <ul style={{ margin: '0.15rem 0 0 1.15rem', padding: 0 }}>
                        {cat.normas_ensayo.map((n, ni) => <li key={ni}>{n}</li>)}
                      </ul>
                    </div>
                  )}
                  {cat.confirmacion_fabricante && cat.confirmacion_fabricante !== 'No aplicable' && (
                    <div><strong>Confirmación adicional del fabricante:</strong> {cat.confirmacion_fabricante}</div>
                  )}
                </div>
                <Field label="Productos concretos seleccionados en obra">
                  <Textarea value={cat.notas} rows={2}
                    onChange={v => setField(['contenido_json', 'productos_por_categoria', i, 'notas'], v)}
                    placeholder="Ej. Pintura acrílica base agua Titan A0 — COV 12 g/L. Ficha técnica adjunta en Storage. Certificado EMICODE EC1 Plus." />
                </Field>
              </>
            )}
          </div>
        ))}
      </Card>

      {/* ─── 5 · Compromiso post-construcción ───────────────────────────────── */}
      <Card title="5 · Compromiso post-construcción (puntos 3.a, 3.b y nivel ejemplar)" icon={CheckCircle}>
        <div style={{ fontSize: '0.78rem', color: C.muted, fontFamily: FF, marginBottom: '0.75rem' }}>
          Para el <strong>3.er punto</strong> del criterio SyB 2, se debe realizar la medición de formaldehído y COVT en el edificio ya terminado y sin ocupar. El nivel ejemplar del criterio exige umbrales más restrictivos.
        </div>

        <div style={{ display: 'grid', gap: '0.4rem', marginBottom: '0.75rem' }}>
          {[
            { key: 'punto_2_productos', label: 'Punto 2 · Todos los productos instalados de las categorías A-H aplicables cumplen la Tabla 13' },
            { key: 'punto_3a_covt', label: 'Punto 3.a · Medición post-construcción de COVT · umbral < 300 μg/m³ durante 8 h' },
            { key: 'punto_3b_formaldehido', label: 'Punto 3.b · Medición post-construcción de formaldehído · umbral ≤ 100 μg/m³ durante 30 min (OMS)' },
            { key: 'ejemplar_formaldehido', label: 'Nivel ejemplar · Formaldehído ≤ 10 μg/m³ (referencia OMS reducida)' },
            { key: 'ejemplar_covt', label: 'Nivel ejemplar · COVT ≤ 60 μg/m³' },
          ].map(opt => {
            const marcada = !!pc[opt.key]
            return (
              <label key={opt.key} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                padding: '0.4rem 0.55rem', border: `1px solid ${marcada ? C.olive : C.border}`,
                borderRadius: '4px', background: marcada ? '#F5F8ED' : 'white',
                cursor: 'pointer', fontFamily: FF,
              }}>
                <input type="checkbox" checked={marcada}
                  onChange={e => setField(['contenido_json', 'post_construccion', opt.key], e.target.checked)}
                  style={{ marginTop: '0.2rem' }} />
                <span style={{ fontSize: '0.82rem', color: C.text, lineHeight: 1.4 }}>{opt.label}</span>
              </label>
            )
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <Field label="Métodos de ensayo aplicables" span={2}
            hint="Métodos oficiales del manual BREEAM ES NC 2015. Un método por línea.">
            <Textarea rows={4} value={(pc.metodos_ensayo || []).join('\n')}
              onChange={v => setField(['contenido_json', 'post_construccion', 'metodos_ensayo'], v.split('\n').filter(x => x.trim()))} />
          </Field>
          <Field label="Laboratorio de ensayo (acreditación ENAC recomendada)" span={2}>
            <TextInput value={pc.laboratorio}
              onChange={v => setField(['contenido_json', 'post_construccion', 'laboratorio'], v)}
              placeholder="Ej. Applus+ Laboratorios · nº acreditación ENAC 26/LE0207" />
          </Field>
          <Field label="Compromiso en caso de superar los umbrales" span={2}
            hint="El manual (página 80) exige que, si las mediciones superan los límites, el equipo se comprometa a tomar medidas correctoras y volver a medir.">
            <Textarea rows={2} value={pc.compromiso_reduccion}
              onChange={v => setField(['contenido_json', 'post_construccion', 'compromiso_reduccion'], v)} />
          </Field>
        </div>
      </Card>

      {/* ─── 6 · Medidas SMACNA (opcional) ──────────────────────────────────── */}
      <Card title="6 · Medidas de gestión de obra (SMACNA · opcional)" icon={ClipboardText}
        action={(
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem', color: C.text, fontFamily: FF, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!cj.aplica_smacna}
              onChange={e => setField(['contenido_json', 'aplica_smacna'], e.target.checked)} />
            El pliego del proyecto lo exige
          </label>
        )}>
        <div style={{ fontSize: '0.78rem', color: C.muted, fontFamily: FF, marginBottom: '0.75rem' }}>
          Los 6 apartados SMACNA (basados en LEED) no son obligatorios en el Manual BREEAM ES, pero muchos pliegos técnicos españoles (ej. LT02 de MAD2) los incorporan como buena práctica. Activa esta sección si el pliego del proyecto los exige.
        </div>
        {cj.aplica_smacna ? (
          (cj.medidas_smacna || []).map((ap, i) => {
            const apDef = PPCAI_SMACNA_APARTADOS[i]
            return (
              <div key={ap.codigo} style={{ padding: '0.6rem 0.75rem', border: `1px solid ${C.border}`, borderRadius: '4px', marginBottom: '0.5rem' }}>
                <div style={{ fontFamily: FF, fontWeight: '600', fontSize: '0.85rem', color: C.text, marginBottom: '0.5rem' }}>
                  6.{ap.codigo} · {apDef?.titulo || ap.titulo}
                </div>
                <div style={{ display: 'grid', gap: '0.3rem', marginBottom: '0.5rem' }}>
                  {(apDef?.medidas || []).map((med, mi) => {
                    const marcada = (ap.medidas_seleccionadas || []).includes(mi)
                    return (
                      <label key={mi} style={{
                        display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                        padding: '0.35rem 0.5rem', border: `1px solid ${marcada ? C.olive : C.border}`,
                        borderRadius: '3px', background: marcada ? '#F5F8ED' : 'white',
                        cursor: 'pointer', fontFamily: FF,
                      }}>
                        <input type="checkbox" checked={marcada}
                          onChange={() => {
                            const cur = ap.medidas_seleccionadas || []
                            const next = marcada ? cur.filter(x => x !== mi) : [...cur, mi].sort((a, b) => a - b)
                            setField(['contenido_json', 'medidas_smacna', i, 'medidas_seleccionadas'], next)
                          }}
                          style={{ marginTop: '0.2rem' }} />
                        <span style={{ fontSize: '0.8rem', color: C.text, lineHeight: 1.4 }}>{med}</span>
                      </label>
                    )
                  })}
                </div>
                <Field label="Medidas adicionales del proyecto">
                  <Textarea rows={2} value={ap.medidas_adicionales}
                    onChange={v => setField(['contenido_json', 'medidas_smacna', i, 'medidas_adicionales'], v)} />
                </Field>
              </div>
            )
          })
        ) : (
          <div style={{ fontSize: '0.78rem', color: C.muted, fontFamily: FF, fontStyle: 'italic', textAlign: 'center', padding: '0.75rem' }}>
            Sección desactivada · marca «El pliego del proyecto lo exige» arriba para editar los 6 apartados.
          </div>
        )}
      </Card>

      {/* ─── 7 · Firmante + notas del asesor ────────────────────────────────── */}
      <Card title="7 · Firmante técnico del plan" icon={CheckCircle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <Field label="Nombre y apellidos">
            <TextInput value={local.firmante_nombre}
              onChange={v => setField(['firmante_nombre'], v)} />
          </Field>
          <Field label="Cargo">
            <TextInput value={local.firmante_cargo}
              onChange={v => setField(['firmante_cargo'], v)}
              placeholder="Responsable BREEAM del contratista, Jefe de obra..." />
          </Field>
          <Field label="Email de contacto">
            <TextInput type="email" value={local.firmante_email}
              onChange={v => setField(['firmante_email'], v)} />
          </Field>
          <Field label="Fecha de firma">
            <TextInput type="date" value={local.firma_fecha}
              onChange={v => setField(['firma_fecha'], v)} />
          </Field>
        </div>
      </Card>

      <Card title="Notas del asesor BREEAM" icon={ClipboardText}>
        <Field label="Notas, observaciones o desviaciones específicas del proyecto">
          <Textarea rows={4} value={cj.notas_asesor}
            onChange={v => setField(['contenido_json', 'notas_asesor'], v)}
            placeholder="Cualquier información complementaria que el asesor considere relevante para el expediente." />
        </Field>
      </Card>
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════════════
//   GENERADOR IA · borrador del PPCAI a partir de documentos analizados
//   Estructura fiel al Manual BREEAM ES NC 2015 · SyB 2
// ═══════════════════════════════════════════════════════════════════════
async function generarBorradorPPCAI(plan, contratistaPrincipal, documentos) {
  // Recolectar hallazgos relevantes (aire interior, acabados, materiales,
  // ventilación, residuos) de los documentos ya analizados en Fase 2.
  const hallazgosRelevantes = []
  const sistemasAcabados = []
  for (const d of (documentos || [])) {
    if (d.analisis_estado !== 'completado') continue
    for (const h of (d.hallazgos_extraidos || [])) {
      if (['aire_interior', 'acabados', 'materiales', 'gestion', 'residuos', 'climatizacion', 'ventilacion'].includes(h.tema)) {
        hallazgosRelevantes.push({
          documento: d.nombre,
          tema: h.tema,
          hallazgo: h.hallazgo,
          cita: (h.cita_literal || '').substring(0, 200),
          pagina: h.pagina,
          criterios: h.criterios_breeam_relacionados,
        })
      }
    }
    for (const s of (d.sistemas_constructivos || [])) {
      if (s.categoria === 'acabados' || (s.criterios_breeam_relacionados || []).includes('SyB 02') || (s.criterios_breeam_relacionados || []).includes('SyB 2')) {
        sistemasAcabados.push({
          documento: d.nombre,
          categoria: s.categoria,
          descripcion: (s.descripcion || '').substring(0, 200),
          materiales: s.materiales_mencionados,
          certificaciones: s.certificaciones_mencionadas,
          pagina: s.pagina,
        })
      }
    }
  }

  const contextoDocumentos = hallazgosRelevantes.length + sistemasAcabados.length > 0
    ? `\n\nHALLAZGOS ya extraídos por la IA de los documentos del proyecto (úsalos para pre-rellenar el plan):\n\n${JSON.stringify({ hallazgos: hallazgosRelevantes.slice(0, 30), sistemas_acabados: sistemasAcabados.slice(0, 15) }, null, 2)}`
    : '\n\nNo hay hallazgos previos de la Fase 2 · genera un borrador con las medidas típicas de un proyecto de edificación estándar.'

  const contratistaTxt = contratistaPrincipal?.nombre
    ? `El contratista principal es «${contratistaPrincipal.nombre}» (rol: ${contratistaPrincipal.rol}).`
    : 'El contratista principal aún no está definido.'

  const prompt = `Eres un asesor BREEAM ES NC 2015 senior con dominio del Manual Técnico oficial (IPC-BREEAM-01-12 · ED.02 · 31/03/2017). Vas a generar un borrador estructurado del Plan de Prevención y Control de la Calidad del Aire Interior (PPCAI) para el criterio SyB 2, siguiendo la estructura oficial del manual (páginas 70-81).

CONTEXTO DEL PROYECTO:
${contratistaTxt}${contextoDocumentos}

REFERENCIA DEL MANUAL:
El PPCAI del criterio SyB 2 se compone de 5 ELEMENTOS oficiales (a-e):
  a) Eliminación de las fuentes de contaminación
  b) Reducción y control de las fuentes de contaminación
  c) Procedimientos de purga antes de la ocupación
  d) Ensayos y análisis de terceros (post-construcción)
  e) Mantenimiento de la calidad del aire interior en uso
Además del punto 1 del manual (el PPCAI en sí), hay criterios adicionales sobre distancias del sistema HVAC (>10 m tomas-salidas, >20 m fuentes externas), diseño de ventilación conforme a UNE-EN 13779:2008, y sensores de CO₂ en zonas de ocupación elevada.
La Tabla 13 del manual (categorías A-H) fija los requisitos COV/formaldehído por tipo de producto.
El punto 3 exige medición post-construcción · formaldehído ≤ 100 μg/m³ (30 min) y COVT < 300 μg/m³ (8 h). Nivel ejemplar: formaldehído ≤ 10 μg/m³ y COVT ≤ 60 μg/m³.

TAREA: Devuelve UN JSON válido con esta estructura exacta:

{
  "objetivo": "Objetivo del plan · 2-3 frases · adapta al proyecto",
  "ambito": "Ámbito de aplicación · 1-2 frases",
  "cronograma": "Cronograma de implementación · 1-2 frases",
  "puntos_objetivo": 3,
  "elementos_manual": [
    { "codigo": "a", "medidas_seleccionadas": [0,1,2,3,4], "medidas_adicionales": "", "responsable": "Jefe de obra", "frecuencia_control": "semanal" },
    { "codigo": "b", "medidas_seleccionadas": [0,1,2], "medidas_adicionales": "", "responsable": "Responsable BREEAM del contratista", "frecuencia_control": "diaria" },
    { "codigo": "c", "medidas_seleccionadas": [0,1,2], "medidas_adicionales": "", "responsable": "Jefe de obra", "frecuencia_control": "por_hito" },
    { "codigo": "d", "medidas_seleccionadas": [0,1,2,3], "medidas_adicionales": "", "responsable": "Asesor BREEAM + laboratorio ENAC", "frecuencia_control": "por_hito" },
    { "codigo": "e", "medidas_seleccionadas": [0,1,2], "medidas_adicionales": "", "responsable": "Facility manager del edificio", "frecuencia_control": "mensual" }
  ],
  "criterios_adicionales": {
    "distancias_hvac": { "aplica": true|false, "tomas_salidas_10m": true|false, "tomas_fuentes_20m": true|false, "justificacion": "..." },
    "diseno_ventilacion": { "cumple_une13779": true|false, "caudales_verificados": "...", "referencia_calculo": "..." },
    "sensores_co2": { "aplica": true|false, "zonas": ["..."], "conectado_ventilacion": true|false, "justificacion": "..." }
  },
  "productos_por_categoria": [
    { "codigo": "A", "aplica": true,  "notas": "Producto concreto si aparece en el contexto, o descripción genérica." },
    { "codigo": "B", "aplica": true,  "notas": "..." },
    { "codigo": "C", "aplica": false, "notas": "" },
    { "codigo": "D", "aplica": false, "notas": "" },
    { "codigo": "E", "aplica": true,  "notas": "..." },
    { "codigo": "F", "aplica": true,  "notas": "..." },
    { "codigo": "G", "aplica": true,  "notas": "..." },
    { "codigo": "H", "aplica": true,  "notas": "..." }
  ],
  "post_construccion": {
    "punto_2_productos": true|false,
    "punto_3a_covt": true|false,
    "punto_3b_formaldehido": true|false,
    "ejemplar_covt": true|false,
    "ejemplar_formaldehido": true|false,
    "laboratorio": "Nombre del laboratorio si se conoce, si no ''",
    "compromiso_reduccion": "1-2 frases con el compromiso ante superación de umbrales"
  },
  "aplica_smacna": true|false,
  "medidas_smacna": [
    { "codigo": "1", "medidas_seleccionadas": [0,1,2], "medidas_adicionales": "" },
    { "codigo": "2", "medidas_seleccionadas": [0,1], "medidas_adicionales": "" },
    { "codigo": "3", "medidas_seleccionadas": [0,1], "medidas_adicionales": "" },
    { "codigo": "4", "medidas_seleccionadas": [0,1], "medidas_adicionales": "" },
    { "codigo": "5", "medidas_seleccionadas": [0,1], "medidas_adicionales": "" },
    { "codigo": "6", "medidas_seleccionadas": [0,1], "medidas_adicionales": "" }
  ],
  "notas_asesor": "Notas breves con observaciones específicas del proyecto"
}

INDICACIONES DE PRE-RELLENO:
- "puntos_objetivo": si el contexto no dice nada, usa 3 (aspirar al máximo del criterio).
- "elementos_manual": los índices de "medidas_seleccionadas" son 0-based. En un proyecto estándar suelen aplicar TODAS las medidas de cada elemento (marca todos los índices). Solo excluye alguna si el contexto lo desmiente.
- "criterios_adicionales.distancias_hvac.aplica" = true solo si el proyecto es climatizado (aire acondicionado).
- "criterios_adicionales.sensores_co2.aplica" = true solo si el proyecto tiene salas de reuniones grandes, auditorios, gimnasios, comercios, aulas o cines. Zonas típicas: ${JSON.stringify(PPCAI_ZONAS_OCUPACION_ELEVADA)}
- "productos_por_categoria": C (estructuras de madera) y D (parqué) suelen NO APLICAR salvo que el proyecto los mencione explícitamente.
- "aplica_smacna": marca true solo si el pliego del contratista menciona explícitamente SMACNA, LEED IEQ 3.1 o los 6 apartados (protección HVAC, fuentes emisoras, rutas contaminantes, limpieza, materiales absorbentes, cronograma).
- Idioma: español · terminología técnica BREEAM.
- Cuando cites productos, ampliáte con la ficha técnica si el contexto la aporta (COV g/L, certificación, fabricante).

REGLAS DE OUTPUT: JSON válido único, sin markdown, sin texto antes ni después. Numéricos sin comillas. Booleanos en minúsculas.`

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim()
  const functionUrl = `${supabaseUrl}/functions/v1/breeam-analyzer`

  const apiData = await callBreeamAnalyzer(functionUrl, anonKey, {
    file_infos: [],   // no adjuntamos documentos · toda la información viene en el prompt
    direct_files: [],
    prompt_text: prompt,
    model: 'claude-haiku-4-5-20251001',
    max_tokens: 12000,
  })
  if (apiData?.error) throw new Error(apiData.error)

  const rawText = apiData.content?.map(c => c.text || '').join('') || ''
  const generado = parseAiJson(rawText)

  // Reconstruir el contenido_json fusionando el esqueleto base con lo que
  // la IA genera, preservando siempre los textos oficiales del manual
  // (títulos, normas, criterios de comportamiento etc.).
  const base = ppcaiVacio()

  const mergedCA = { ...base.criterios_adicionales }
  const gCA = generado.criterios_adicionales || {}
  if (gCA.distancias_hvac)     mergedCA.distancias_hvac     = { ...base.criterios_adicionales.distancias_hvac,     ...gCA.distancias_hvac }
  if (gCA.diseno_ventilacion)  mergedCA.diseno_ventilacion  = { ...base.criterios_adicionales.diseno_ventilacion,  ...gCA.diseno_ventilacion }
  if (gCA.sensores_co2)        mergedCA.sensores_co2        = { ...base.criterios_adicionales.sensores_co2,        ...gCA.sensores_co2 }

  return {
    ...base,
    objetivo:        generado.objetivo    || base.objetivo,
    ambito:          generado.ambito      || base.ambito,
    cronograma:      generado.cronograma  || base.cronograma,
    puntos_objetivo: [1, 2, 3].includes(generado.puntos_objetivo) ? generado.puntos_objetivo : base.puntos_objetivo,
    elementos_manual: base.elementos_manual.map((el) => {
      const g = (generado.elementos_manual || []).find(x => x.codigo === el.codigo) || {}
      return {
        ...el,
        medidas_seleccionadas: Array.isArray(g.medidas_seleccionadas) ? g.medidas_seleccionadas : [],
        medidas_adicionales: g.medidas_adicionales || '',
        responsable: g.responsable || '',
        frecuencia_control: g.frecuencia_control || '',
      }
    }),
    criterios_adicionales: mergedCA,
    productos_por_categoria: base.productos_por_categoria.map(cat => {
      const g = (generado.productos_por_categoria || []).find(x => x.codigo === cat.codigo) || {}
      return {
        ...cat,
        aplica: typeof g.aplica === 'boolean' ? g.aplica : cat.aplica,
        notas: g.notas || '',
      }
    }),
    post_construccion: {
      ...base.post_construccion,
      ...(generado.post_construccion || {}),
      // mantener siempre los métodos de ensayo del manual
      metodos_ensayo: base.post_construccion.metodos_ensayo,
    },
    aplica_smacna: !!generado.aplica_smacna,
    medidas_smacna: base.medidas_smacna.map((ap) => {
      const g = (generado.medidas_smacna || []).find(x => x.codigo === ap.codigo) || {}
      return {
        ...ap,
        medidas_seleccionadas: Array.isArray(g.medidas_seleccionadas) ? g.medidas_seleccionadas : [],
        medidas_adicionales: g.medidas_adicionales || '',
      }
    }),
    notas_asesor: generado.notas_asesor || '',
  }
}
